/**
 * L'elenco delle schermate: **una sola fonte** per il menu, per le rotte e per
 * i permessi.
 *
 * Tenerlo in un posto solo non è ordine per l'ordine: menu, rotte e ruoli
 * devono dire la stessa cosa, e tre elenchi paralleli divergono alla prima
 * schermata aggiunta di fretta — una voce che porta a un 404, o peggio una
 * rotta raggiungibile che nel menu non compare.
 */

import type { ReactElement } from 'react';
import { seesScreen, type Role } from '@errebi/domain';

import type { VoceMenu } from '../components/side-menu';
import { FineLinea } from './end-of-line';
import { Guida } from './guide';
import { Magazzino } from './warehouse';
import { Prelievi } from './picking';
import { Negozio } from './store';
import { Produzione } from './production';
import { Ricezione } from './receiving/receiving';
import { Scarico } from './unloading';

export interface Schermata extends VoceMenu {
  /** Il percorso nell'URL. È anche la chiave: due nomi per la stessa cosa divergono. */
  readonly percorso: string;
  readonly ruoli: readonly Role[];
  readonly c: () => ReactElement;
}

/**
 * L'ordine è quello in cui la merce attraversa l'azienda, e i `gruppo`
 * seguono lo stesso filo. È l'unico ordine che chi ci lavora non deve imparare.
 */
export const SCHERMATE: readonly Schermata[] = [
  {
    k: 'ricezione',
    percorso: '/ricezione',
    nome: 'Banco',
    sigla: 'BAN',
    gruppo: 'Ricezione',
    ruoli: ['ricezione'],
    c: () => <Ricezione />,
  },
  {
    k: 'carico',
    percorso: '/carico',
    nome: 'Carico',
    sigla: 'CAR',
    gruppo: 'Magazzino',
    ruoli: ['magazzino'],
    c: () => <Magazzino />,
  },
  {
    k: 'scarico',
    percorso: '/unloading',
    nome: 'Scarico',
    sigla: 'SCA',
    gruppo: 'Magazzino',
    ruoli: ['magazzino'],
    c: () => <Scarico />,
  },
  {
    k: 'negozio',
    percorso: '/negozio',
    nome: 'Negozio',
    sigla: 'NEG',
    gruppo: 'Magazzino',
    ruoli: ['magazzino'],
    c: () => <Negozio />,
  },
  {
    k: 'prelievi',
    percorso: '/picking',
    nome: 'Prelievi',
    sigla: 'PRE',
    gruppo: 'Magazzino',
    ruoli: ['magazzino'],
    c: () => <Prelievi />,
  },
  {
    k: 'dtf',
    percorso: '/stampa-dtf',
    nome: 'Stampa DTF',
    sigla: 'DTF',
    gruppo: 'Produzione',
    ruoli: ['produzione'],
    c: () => <Produzione reparto="DTF" />,
  },
  {
    k: 'dtg',
    percorso: '/stampa-dtg',
    nome: 'Stampa DTG',
    sigla: 'DTG',
    gruppo: 'Produzione',
    ruoli: ['produzione'],
    c: () => <Produzione reparto="DTG" />,
  },
  {
    k: 'ricamo',
    percorso: '/ricamo',
    nome: 'Ricamo',
    sigla: 'RIC',
    gruppo: 'Produzione',
    ruoli: ['produzione'],
    c: () => <Produzione reparto="WEMB" />,
  },
  {
    k: 'fine-linea',
    percorso: '/end-of-line',
    nome: 'Fine linea',
    sigla: 'FIN',
    gruppo: 'Spedizione',
    ruoli: ['fine-linea'],
    c: () => <FineLinea />,
  },
  {
    k: 'guida',
    percorso: '/guida',
    nome: 'Guida',
    sigla: 'GUI',
    // Fuori dai quattro gruppi del flusso, e in coda: la guida non è un momento
    // della merce, è il piede della colonna. Va aggiunta anche a `GRUPPI` in
    // `side-menu.tsx`, o la rotta funziona e la voce non compare.
    gruppo: 'Guida',
    // Tutti e cinque: non esiste un valore «chiunque», e una guida che un ruolo
    // non apre è una guida che quel ruolo non ha.
    ruoli: ['ricezione', 'produzione', 'magazzino', 'fine-linea', 'amministratore'],
    c: () => <Guida />,
  },
];

/**
 * Le schermate che un insieme di ruoli può aprire.
 *
 * Chi vede cosa lo decide `vedeSchermata` in `@errebi/domain`: è un permesso, e
 * i permessi stanno lì. Era la stessa riga anche in ufficio, e i due gemelli
 * avevano già firme diverse su `ruoli` — `string[]` qui, `Ruolo[]` di là, cioè
 * il banco accettava un ruolo che non esiste senza che nulla protestasse.
 */
export function schermatePermesse(ruoli: readonly Role[]): Schermata[] {
  return SCHERMATE.filter((s) => seesScreen(ruoli, s.ruoli));
}

export function schermataDa(percorso: string): Schermata | undefined {
  return SCHERMATE.find((s) => s.percorso === percorso);
}
